// rockField.ts — the boulders.
//
// What makes a Martian surface read as Mars is not the ground texture. It is the
// scatter: thousands of rocks, size-graded, dense on the broken ground and
// sparse on the pan. The question is the one the terrain lane keeps asking —
// what do we know that constrains the invention?
//
// The population. Martian rock size-frequency follows an exponential family
// established from Viking, Pathfinder and the rover landing-site safety work
// (Golombek & Rapp 1997; Golombek et al. 2003, 2008): the cumulative fraction of
// ground covered by rocks of diameter >= D is
//
//     F(D) = k * exp(-q(k) * D),      q(k) = a + b / k
//
// with k the total rock abundance. One parameter, and the bake measures it per
// plot by counting the boulders the orthoimage actually resolves — see
// tools/dtm/rocks.py, which also decides whether that count is trustworthy and
// says so in the metadata.
//
// `a` and `b` are the FIT, and they are Mars's: 1.79 and 0.152, from the landing
// -site work above. They arrive as a `RockLaw` off the focused body's own
// `bake.rocks` in public/art/atlas/bodies.json rather than being written here,
// because on a body with no fit of its own the number the Mars coefficients
// return is meaningless rather than merely wrong. Such a body declares
// `k_source: 'none'`, this module scatters nothing for it, and the Atlas's
// honesty overlay says so on screen.
//
// EVERY INDIVIDUAL ROCK HERE IS INVENTED. THE POPULATION IS NOT.
//
// ── Why bands ────────────────────────────────────────────────────────────────
//
// A single minimum diameter across the whole visible disc cannot work. The
// ground ten metres away wants pebbles; the ground half a kilometre away wants
// only boulders, because nothing smaller survives a pixel. Spending one budget
// at one size on both starves the near field and wastes instances on the far
// one — which is exactly what "a touch of pepper-like noise" looks like.
//
// So rocks are generated in fixed DIAMETER BANDS, each with its own draw
// distance, each from its own PRNG sub-stream. Level of detail becomes a
// question of which bands are drawn, and because the streams are independent,
// dropping a band leaves every larger rock exactly where it was. Boulders can
// never pop. The band that does appear and disappear is the one whose members
// are sub-pixel at the distance where it switches.

import * as THREE from 'three';
import type { PlotField } from './plotField';
import { DEFAULT_PALETTE, SPECIES, paletteFor, type RockSpecies } from './rockSpecies.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';

/** Rock abundance the bake measured, plus how much to believe it. */
export interface RockStats {
  k: number;
  reliable: boolean;
  spread: number;
  d_limit_m: number;
  /** What to scatter with — the plot's own k where it was measurable, else the
   *  site median. Use this, not `k`: on a bedform-dominated plot the raw fit is
   *  the detection threshold talking and runs several times high. */
  k_used: number;
  /** `'none'` is the bake saying no law was fitted for this body, so nothing
   *  was counted and nothing is scattered. */
  k_source: 'measured' | 'site-median' | 'none';
}

/**
 * The body's own Golombek-form fit, `q(k) = a + b/k`, off its registry row's
 * `bake.rocks`. `source` names the fit so the overlay can attribute it.
 */
export interface RockLaw {
  readonly source: string;
  readonly a: number;
  readonly b: number;
}

/**
 * Diameter bands, near to far.
 *
 * `reach` is where the band stops being drawn, chosen so its members are around
 * a pixel there: a 0.25 m rock at 60 m is about the same angular size as a
 * 1.5 m rock at 400 m. `budget` caps the instances a band may spend; if the
 * ground is rocky enough to exceed it the band's radius shrinks rather than the
 * count blowing out, so dense terrain degrades by drawing a smaller circle of
 * correct density instead of a large circle of wrong density.
 */
const BANDS = [
  { lo: 0.10, hi: 0.25, reach: 60, budget: 9000 },
  { lo: 0.25, hi: 0.60, reach: 160, budget: 8000 },
  { lo: 0.60, hi: 1.50, reach: 450, budget: 6000 },
  { lo: 1.50, hi: 12.0, reach: 1400, budget: 2000 },
] as const;

/**
 * Instances a single `fillBand` call may place before it hands the frame back.
 *
 * The predecessor bounded a slice by a CELL count (four), on the reasoning
 * that a band's cells are sorted nearest-first and the near band's whole
 * 9,000-rock budget goes in its first few. That was still a cell boundary,
 * not an instance one: a dense cell can hold most of a band's own budget by
 * itself, so four cells still read as a whole-batch-in-one-frame hit on
 * broken ground. Counting instances and pausing mid-cell bounds the WORK
 * rather than the cell count; the RNG a cell draws from is a plain closure
 * (see `rng`), so resuming it mid-cell costs nothing and places the same
 * rocks in the same order a single-frame fill would have.
 */
const FILL_SLICE_INSTANCES = 250;

/**
 * Knobs, so the look can be found by eye and then written down.
 *
 * Defaults are all 1.0 and mean "exactly what the measurement says". Every one
 * of these is a departure from the data, which is the point — the abundance is
 * measured and the LOOK is a judgement, and the two should not be confused with
 * each other. `toMarkdown()` prints whatever you settle on.
 */
export interface RockTuning {
  /** Multiplies the population count. 1 = the measured abundance. */
  density: number;
  /** Multiplies every diameter. 1 = the law's own sizes. */
  size: number;
  /** Widens the size distribution in log space. 1 = the law; >1 = more range
   *  between the smallest and largest rock in view. */
  spread: number;
  /** Degrees of slope at which rock density saturates. Lower concentrates
   *  rocks on the broken ground harder. */
  slopeDeg: number;
  /** Fraction of a rock's height buried in the ground. */
  burial: number;
  /** Total instances across all bands. */
  budget: number;
}

export const ROCK_DEFAULTS: RockTuning = {
  density: 1, size: 1, spread: 1, slopeDeg: 20, burial: 0.18, budget: 25000,
};

export function rockTuningMarkdown(t: RockTuning): string {
  const d = ROCK_DEFAULTS;
  const row = (k: keyof RockTuning, note: string) =>
    `| \`${k}\` | ${t[k]} | ${d[k]} | ${note} |`;
  return [
    '| knob | value | default | meaning |',
    '|---|---|---|---|',
    row('density', 'population count x measured abundance'),
    row('size', 'diameter multiplier'),
    row('spread', 'size range, log-space (>1 = wider)'),
    row('slopeDeg', 'slope at which density saturates'),
    row('burial', 'fraction of height buried'),
    row('budget', 'total instances across all bands'),
  ].join('\n');
}

/**
 * Side of the deterministic scatter cell, in metres.
 *
 * Following the camera would normally mean the scatter reshuffles as you walk.
 * It does not, because rocks are generated PER CELL from a hash of the cell's
 * own coordinates and the band index: the set of cells in range changes, the
 * contents of any given cell never do. Walk away from a boulder and back, and
 * it is the same boulder — for you and for everyone else.
 */
const CELL_M = 32;

function cellSeed(cx: number, cz: number, band: number, seed: number): number {
  let h = (cx * 73856093) ^ (cz * 19349663) ^ (band * 2654435761) ^ (seed * 83492791);
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^ (h >>> 16)) >>> 0;
}

/** q(k) from the exponential model, on this body's own coefficients. */
function qOfK(k: number, law: RockLaw): number {
  return law.a + law.b / Math.max(k, 1e-4);
}

/**
 * Rocks per square metre with diameter >= d.
 *
 * The model describes covered AREA, so a count divides by the area of a rock.
 * The integral has no elementary form and diverges as d -> 0 — the honest
 * statement that there is no smallest rock — so it is evaluated numerically
 * from a floor the caller must supply.
 */
export function numberDensityGe(d: number, k: number, law: RockLaw, dMax = 12): number {
  if (d <= 0) return Infinity;
  const q = qOfK(k, law);
  // Matches the sample count tools/dtm/rocks.py integrates with. At 512 the two
  // drifted 1.4% apart at small diameters, where the 1/D^2 integrand is
  // steepest — harmless for a scatter, but the bake measures k and this
  // consumes it, so the two had better be reading the same law.
  const steps = 2048;
  const hi = Math.max(dMax, d * 2);
  const h = (hi - d) / steps;
  let sum = 0;
  for (let i = 0; i <= steps; i++) {
    const x = d + i * h;
    const f = (k * q * Math.exp(-q * x)) / ((Math.PI * x * x) / 4);
    sum += f * (i === 0 || i === steps ? 0.5 : 1);
  }
  return sum * h;
}

/** Population of one band, per square metre, after the density knob. */
function bandDensity(b: { lo: number; hi: number }, k: number, law: RockLaw,
                     t: RockTuning): number {
  const raw = numberDensityGe(b.lo, k, law) - numberDensityGe(b.hi, k, law);
  return Math.max(0, raw) * Math.max(0, t.density);
}

/**
 * Apply the size knobs to a diameter drawn from the law.
 *
 * `spread` works in LOG space about the band's own geometric centre, so
 * widening the distribution pushes the small rocks smaller and the large ones
 * larger without moving the middle — which is what "the size range is a bit
 * narrow" actually asks for. A linear multiplier would just make everything
 * bigger and leave the ratio between the biggest and smallest rock unchanged.
 */
function shapeDiameter(d: number, b: { lo: number; hi: number },
                       t: RockTuning): number {
  let out = d;
  if (t.spread !== 1) {
    const mid = Math.sqrt(b.lo * b.hi);
    out = mid * Math.pow(d / mid, Math.max(0.05, t.spread));
  }
  out *= Math.max(0.01, t.size);
  // A wide spread runs the top band toward sixty metres, which is not a rock,
  // it is a hill with the wrong shading. The largest boulder anyone has
  // photographed on Mars is a few metres; fifteen is already generous.
  return Math.min(15, Math.max(0.02, out));
}

/** mulberry32 — small, fast, and identical everywhere, which is the point. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A lumpy, faceted rock.
 *
 * Subdivision 0 — a bare icosahedron, 20 faces. One of these is a handful of
 * pixels even at the closest the Atlas gets, and what carries it is silhouette
 * and the fact that it catches light differently from the ground, not its own
 * curvature. Faceted on purpose: the REGOLITH lesson is that micro-relief has
 * to catch grazing light, and smooth-shaded blobs do not.
 */
/** Value noise on a 2D lattice — the CPU twin of the shader's rkN. */
function vnoise2(x: number, y: number, seed: number): number {
  const h = (ix: number, iy: number) => {
    let n = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(seed, 2246822519);
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
  };
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  let fx = x - ix;
  let fy = y - iy;
  fx = fx * fx * (3 - 2 * fx);
  fy = fy * fy * (3 - 2 * fy);
  return (
    (h(ix, iy) * (1 - fx) + h(ix + 1, iy) * fx) * (1 - fy) +
    (h(ix, iy + 1) * (1 - fx) + h(ix + 1, iy + 1) * fx) * fy
  );
}

/**
 * How finely to tessellate a rock of a given size.
 *
 * A twenty-face icosahedron is plenty for a pebble and reads as a crystal at
 * four metres — which is what "odd gaps when we expand them" was. Deriving the
 * subdivision from the band's actual largest diameter means the SIZE slider
 * pulls detail along with it instead of magnifying a blob.
 */
function detailForSize(maxD: number): number {
  if (maxD < 0.45) return 0;   //   20 faces
  if (maxD < 1.30) return 1;   //   80
  if (maxD < 3.50) return 2;   //  320
  return 3;                    // 1280
}

function rockGeometry(burial: number, detail: number, seed: number,
                     sp: RockSpecies): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(0.5, detail);
  const p = g.getAttribute('position') as THREE.BufferAttribute;
  const v = new THREE.Vector3();

  // Three octaves rather than one random scalar per vertex.
  //
  // Scaling each vertex independently lets neighbours diverge by the full range
  // of the random draw, which pinches the triangles between them into slivers —
  // visible as gaps and spikes the moment a rock is more than a few pixels
  // across. Displacing along a COHERENT field instead means adjacent vertices
  // move together, so the surface stays a surface at every subdivision.
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i).normalize();
    let d = 1.0;
    d += (vnoise2(v.x * 1.7 + seed, v.z * 1.7 - seed, seed) - 0.5) * sp.octaves[0];
    d += (vnoise2(v.x * 4.3 + seed * 2, v.y * 4.3 - seed, seed * 13) - 0.5) * sp.octaves[1];
    d += (vnoise2(v.x * 11.0 + seed * 5, v.z * 11.0 - seed * 3, seed * 31) - 0.5) * sp.octaves[2];
    // Quantising the radius turns smooth lumps into flat fracture planes. Basalt
    // breaks conchoidally and wants all of this; a river cobble has had the
    // facets worn off it and wants none.
    d = (Math.round(d * 11) / 11) * (0.34 * sp.facet) + d * (1 - 0.34 * sp.facet);
    // Flatter where it meets the ground — a rock rests on a face, not a point.
    d *= 1 - 0.30 * Math.max(0, -v.y);
    // Elongation about X, which the instance then rotates to a random heading.
    p.setXYZ(i, v.x * d * 0.5 * sp.elongate, v.y * d * 0.5 * sp.squash, v.z * d * 0.5);
  }
  p.needsUpdate = true;
  // Triplanar in the shader, so the UVs an icosahedron carries are dead weight
  // and would only tempt something into using them.
  g.deleteAttribute('uv');
  // Settle it INTO the ground rather than on it. A rock resting on a surface
  // shows a horizon line where it meets the dirt, and that line is most of what
  // makes a scatter read as settled instead of sprinkled. Baked into the
  // geometry, not the instance position, because only the instance ORIGIN is
  // exaggerated — burying it there would sink rocks deeper every time the
  // relief slider went up.
  g.translate(0, -burial, 0);
  g.computeVertexNormals();
  return g;
}

/**
 * A DEV-only URL switch, the way `?rockshare=0` in src/scene/asteroids.ts is.
 *
 * The rock arms' three controls arm here rather than through a setter: the
 * shading ones key a uniform the first scatter reads, and a switch thrown from
 * outside would land after the field is already built.
 */
function rockDevOff(name: string): boolean {
  return TEST_SWITCHES && typeof location !== 'undefined'
    && new URLSearchParams(location.search).get(name) === '0';
}

function rockMaterial(uniforms: Record<string, THREE.IUniform>,
                      sp: RockSpecies,
                      minDiamPerM: THREE.IUniform,
                      groundTone: THREE.IUniform,
                      litDirect: THREE.IUniform,
                      bounceOn: THREE.IUniform): THREE.ShaderMaterial {
  // Lit by the PLOT's sun, not the scene's.
  //
  // The scene keeps a directional light fixed at (-1, 0.6, 1) for the outer
  // tiers, so a MeshStandardMaterial here would be lit from a constant
  // direction while the ground beneath it swung with the sol slider. Sharing
  // the surface's uniform OBJECTS by reference means the scatter cannot fall
  // out of step, because it is reading the same numbers.
  return new THREE.ShaderMaterial({
    uniforms: {
      uSunDir: uniforms['uSunDir']!,
      uSunFlux: uniforms['uSunFlux']!,
      uExag: uniforms['uExag']!,
      uCamLocal: uniforms['uCamLocal']!,
      uSunCol: uniforms['uSunCol']!,
      uSunTint: uniforms['uSunTint']!,
      uSkyCol: uniforms['uSkyCol']!,
      // The ground's own bounce and the orthoimage the ground is painted from,
      // both by reference: a rock is lit by the same three terms the dirt under
      // it is, and takes its tone from the same photograph.
      uBounceCol: uniforms['uBounceCol']!,
      uGroundTex: uniforms['uAlbedo']!,
      uGroundLow: uniforms['uOrthoLow']!,
      uGroundBBox: uniforms['uBBox']!,
      uHasGround: uniforms['uHasAlbedo']!,
      uHasGroundLow: uniforms['uHasOrthoLow']!,
      uGroundMean: uniforms['uOrthoMean']!,
      uGroundTone: groundTone,
      uLitDirect: litDirect,
      uBounceOn: bounceOn,
      uAlbedo: { value: new THREE.Vector3(...sp.albedo) },
      uMottle: { value: sp.mottle },
      uGrain: { value: sp.grain },
      uDust: { value: sp.dust },
      uBedding: { value: sp.bedding },
      uBedCount: { value: sp.beddingCount },
      uMinDiamPerM: minDiamPerM,
    },
    vertexShader: /* glsl */ `
      uniform float uExag;
      uniform float uMinDiamPerM;
      uniform vec3 uCamLocal;
      varying vec3 vN;
      varying vec3 vW;
      varying float vBed;
      varying vec2 vOrigin;
      #include <logdepthbuf_pars_vertex>
      bool isPerspectiveMatrix(mat4 m) { return m[2][3] == -1.0; }
      void main(){
        // The rock's OWN vertical axis, before the instance rotates it. Bedding
        // is laid down in the rock's frame, not the world's — that is what a
        // bed IS — so the lamination coordinate has to be sampled here.
        vBed = position.y;
        #ifdef USE_INSTANCING
          // The patch of ground this rock stands on, which is where its tone
          // comes from. Per INSTANCE and not per fragment, so one rock is one
          // tone however big it draws.
          vOrigin = vec2(instanceMatrix[3].x, instanceMatrix[3].z);
          vec4 p = instanceMatrix * vec4(position, 1.0);
          vN = normalize(mat3(instanceMatrix) * normal);
          // Only the instance POSITION may be exaggerated, never its size —
          // scaling the whole vertex turns a 2.5x relief slider into a field
          // of spikes. Lift the origin, carry the shape along unchanged.
          float baseY = instanceMatrix[3].y;
          p.y = baseY * uExag + (p.y - baseY);
          // Under a pixel, this instance is not drawn AT ALL.
          //
          // A band's reach is a ground radius and it was cut for an eye a
          // person's height over it; from the top of the SURFACE band the same
          // disc is hundreds of metres away and its finer half lands under a
          // pixel each - a lit point on ground the sun does not reach, which is
          // the pepper-like noise the band design exists to avoid. The whole
          // triangle goes outside clip space, so no fragment is ever shaded.
          // uMinDiamPerM is the metres of diameter one pixel covers per metre
          // of range, and is zero wherever no pane has been measured.
          float diamM = length(instanceMatrix[0].xyz);
          vec3 originM = vec3(instanceMatrix[3].x, baseY * uExag, instanceMatrix[3].z);
          if (uMinDiamPerM > 0.0 && diamM < uMinDiamPerM * length(uCamLocal - originM)) {
            gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
            return;
          }
        #else
          vOrigin = position.xz;
          vec4 p = vec4(position, 1.0);
          vN = normal;
          p.y *= uExag;
        #endif
        vW = p.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * p;
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform vec3 uSunDir;
      uniform float uSunFlux;
      uniform vec3 uCamLocal;
      uniform vec3 uAlbedo, uSunCol, uSunTint, uSkyCol, uBounceCol;
      uniform float uMottle, uGrain, uDust;
      uniform float uBedding, uBedCount;
      uniform sampler2D uGroundTex, uGroundLow;
      uniform vec2 uGroundBBox;
      uniform float uHasGround, uHasGroundLow, uGroundMean;
      uniform float uGroundTone, uLitDirect, uBounceOn;
      varying vec3 vN;
      varying vec3 vW;
      varying float vBed;
      varying vec2 vOrigin;

      /** The transfer a tagged sRGB sampler applies, for a texture read raw. */
      float rkSrgbLin(float c){
        return c <= 0.04045 ? c / 12.92 : pow((c + 0.055) / 1.055, 2.4);
      }

      /**
       * How light the ground under this rock is, against its own low-pass.
       *
       * The same pair the surface reads (dtmPlot.ts, "albedo"), sampled at the
       * instance origin. A RATIO and not a colour: the orthoimage is one
       * greyscale band, so this says how light this patch of ground is against
       * the region around it and invents no hue the photograph does not carry.
       */
      float rkGroundTone(vec2 xz){
        if (uHasGround < 0.5) return 1.0;
        vec2 uv = clamp(xz / uGroundBBox + 0.5, 0.0005, 0.9995);
        float o = texture2D(uGroundTex, uv).r;
        float ref = uHasGroundLow > 0.5
          ? max(texture2D(uGroundLow, uv).r, 1e-3)
          : max(uGroundMean, 1e-3);
        float ratio = uHasGroundLow > 0.5
          ? rkSrgbLin(o) / max(rkSrgbLin(ref), 1e-4)
          : o / ref;
        return clamp(ratio, 0.55, 1.70);
      }

      // ── triplanar procedural surface ──────────────────────────────────────
      // The rock carries no UVs — an icosahedron's are useless on a lump, and
      // unwrapping thousands of instanced boulders is not a thing anyone should
      // do. Three planar samples blended by the normal give a seamless surface
      // on any shape, and sampling in PLOT-LOCAL METRES means neighbouring
      // rocks can never repeat: each one occupies different ground.
      float rkH(vec2 p){ p = fract(p * vec2(0.1031, 0.1030)); p += dot(p, p.yx + 33.33); return fract((p.x + p.y) * p.x); }
      float rkN(vec2 p){
        vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
        return mix(mix(rkH(i), rkH(i + vec2(1,0)), f.x),
                   mix(rkH(i + vec2(0,1)), rkH(i + vec2(1,1)), f.x), f.y);
      }
      float rkF(vec2 p){ return rkN(p) * 0.55 + rkN(p * 2.13 + 7.7) * 0.28 + rkN(p * 4.31 + 19.3) * 0.17; }
      float rkTri(vec3 w, vec3 n, float s){
        vec3 b = pow(abs(n), vec3(4.0)); b /= (b.x + b.y + b.z);
        return rkF(w.yz * s) * b.x + rkF(w.xz * s) * b.y + rkF(w.xy * s) * b.z;
      }

      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        vec3 N = normalize(vN);
        float dist = length(uCamLocal - vW);

        // Fine detail has to fade out, or a 0.1 m pebble at eighty metres
        // becomes a pixel-sized noise generator and the whole field crawls.
        float fine = 1.0 - smoothstep(14.0, 70.0, dist);
        float coarse = rkTri(vW, N, 1.9);
        float grain = mix(0.5, rkTri(vW, N, uGrain), fine);

        // Exposed rock is darker and less dusty than the drape it sits in —
        // that value difference is most of what makes a scatter read at all.
        vec3 albedo = uAlbedo * (1.0 - uMottle * 0.5 + uMottle * coarse + 0.18 * grain);

        // ── laminae ───────────────────────────────────────────────────────
        // A sedimentary block's cut edge is a stack of thin layers, and that
        // stripe is most of what says "this was deposited" rather than "this
        // cooled". Sampled on the rock's own vertical axis so the layers stay
        // parallel to its bedding however the instance is turned, and gated by
        // the face normal so it shows on the EDGES — a bedding plane seen
        // face-on is a surface, not a stack.
        if (uBedding > 0.001) {
          float band = sin(vBed * uBedCount * 6.2831853);
          float edge = 1.0 - abs(N.y);
          float lam = (band * 0.5 + 0.5);
          lam = mix(lam, smoothstep(0.35, 0.65, lam), 0.6);
          albedo *= 1.0 - uBedding * edge * (0.42 * (1.0 - lam));
        }

        // Regolith settles on anything facing up and never blows off. This is
        // the cheapest line here and probably the most valuable: it is what
        // makes a boulder look like it has been SITTING in this ground rather
        // than having been dropped on it this morning.
        float up = smoothstep(0.10, 0.85, N.y);
        // Iron-oxide dust, and a rock species' own albedo below it, are the
        // last Mars-tuned constants in the walkaround. Reachable only where a
        // body's row carries a rock law and its region measured an abundance,
        // which today is Mars alone: createRockField refuses otherwise.
        albedo = mix(albedo, vec3(0.35, 0.255, 0.190), up * uDust * (0.75 + 0.80 * coarse));

        // One tone per rock, off the ground it stands on. The species says what
        // the stone IS; the patch says how light this corner of the plot is,
        // and without it every rock of a lithology draws at one value whatever
        // it is sitting in.
        albedo *= mix(1.0, rkGroundTone(vOrigin), uGroundTone);

        // Perturb the normal from the same field, close in only.
        //
        // Note we are spared the trap the reference implementation documents:
        // it had to rotate its gradient because three hands the normal chunk a
        // VIEW-space normal, while the gradient is world-space. Ours is a
        // hand-written shader whose normal and sun are both already in plot
        // space, so the two agree by construction.
        if (fine > 0.01) {
          float e = 0.04;
          float h0 = rkTri(vW, N, 7.0);
          vec3 grad = vec3(rkTri(vW + vec3(e,0,0), N, 7.0) - h0,
                           rkTri(vW + vec3(0,e,0), N, 7.0) - h0,
                           rkTri(vW + vec3(0,0,e), N, 7.0) - h0) / e;
          N = normalize(N - (grad - N * dot(grad, N)) * 0.08 * fine);
        }

        // uLitDirect is 1 everywhere the app draws; the unlit-rocks control
        // takes it to 0, which leaves one value across the whole rock.
        float ci = max(dot(N, uSunDir), 0.0);
        float direct = mix(0.55, ci, uLitDirect);
        float upFace = mix(0.5, max(N.y, 0.0), uLitDirect);
        float downFace = mix(0.5, 0.5 - 0.5 * N.y, uLitDirect);
        vec3 col = albedo * direct * uSunCol * uSunTint * uSunFlux;
        col += albedo * uSkyCol * (0.42 + 0.58 * upFace);
        // The bounce off the lit regolith around it - the same term, the same
        // body palette and the same clock the ground under it already carries
        // (dtmPlot.ts, "sky"). Weighted toward the faces that see dirt rather
        // than sky, which is the opposite of the dome fill. Without it a
        // boulder's shadow side is black while the ground beside it is not.
        col += albedo * uBounceCol * (0.35 + 0.65 * downFace) * uSunFlux * uBounceOn;
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
}

/**
 * The scatter's two programs, linked at the Atlas mount.
 *
 * `warmPlotPrograms` carries the ground, the walls and the surface and no rock,
 * so both of the scatter's own programs used to be first asked for inside the
 * PLOT to SURFACE leg: the colour one when the first band fills, and the shadow
 * one on the frame `setShadowCasting(true)` makes the rocks casters. The mesh
 * here is INSTANCED and CASTS for exactly that reason - `USE_INSTANCING` comes
 * off the object and keys a program of its own, and the depth variant is drawn
 * by the shadow pass, which `renderer.compile` does not walk. Built from the
 * same factory off the same source strings, so three finds both in its cache
 * when the real scatter arrives; `count` is 0, so nothing is ever drawn.
 */
export function warmRockPrograms(): { readonly group: THREE.Group; dispose(): void } {
  // A uniform's VALUE is no part of a program's cache key, and three filters
  // its own upload list by the keys the bag carries. These are the six
  // `rockMaterial` shares with the plot, and their values are never read.
  const uniforms: Record<string, THREE.IUniform> = {
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSunFlux: { value: 1 },
    uExag: { value: 1 },
    uCamLocal: { value: new THREE.Vector3() },
    uSunCol: { value: new THREE.Vector3(1, 1, 1) },
    uSunTint: { value: new THREE.Vector3(1, 1, 1) },
    uSkyCol: { value: new THREE.Vector3(1, 1, 1) },
    uBounceCol: { value: new THREE.Vector3(1, 1, 1) },
    uAlbedo: { value: null },
    uOrthoLow: { value: null },
    uBBox: { value: new THREE.Vector2(1, 1) },
    uHasAlbedo: { value: 0 },
    uHasOrthoLow: { value: 0 },
    uOrthoMean: { value: 0.5 },
  };
  // Any species: the palette varies the UNIFORMS and never the source, so one
  // prototype is every lithology's program.
  const species = SPECIES[DEFAULT_PALETTE[0]!.species]!;
  const material = rockMaterial(uniforms, species, { value: 0 }, { value: 1 }, { value: 1 },
    { value: 1 });
  material.name = 'rock-warm';
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
  geometry.setAttribute('normal',
    new THREE.BufferAttribute(new Float32Array([0, 1, 0, 0, 1, 0, 0, 1, 0]), 3));
  const mesh = new THREE.InstancedMesh(geometry, material, 1);
  mesh.count = 0;
  mesh.castShadow = true;
  mesh.frustumCulled = false;
  const group = new THREE.Group();
  group.name = 'rock-program-warm';
  group.add(mesh);
  return {
    group,
    dispose(): void {
      mesh.dispose();
      material.dispose();
      geometry.dispose();
    },
  };
}

interface Band {
  /** One mesh per species in the palette — geometry AND surface differ per
   *  lithology, so they cannot share an InstancedMesh. */
  meshes: THREE.InstancedMesh[];
  /** Cumulative palette weights, for picking a species from one random draw. */
  cum: number[];
  radius: number;
  /** Centre this band was last generated around, in plot-local metres. */
  cx: number;
  cz: number;
  built: boolean;
}

/**
 * Rocks big enough to be in the way, as a flat array of
 * [x, z, radius, baseY, height] in plot-local metres.
 *
 * Flat rather than objects because a vehicle scans the whole set every frame
 * for six wheels, and because it is rebuilt whenever the scatter is. baseY is
 * UN-exaggerated, matching the height field, so a consumer applies the relief
 * slider itself — the rock's own size is not exaggerated and must not be.
 */
/**
 * How many pixels across the SMALLEST member of a band has to be to be drawn.
 *
 * A band's `reach` is a distance over the GROUND, and it was cut for an eye a
 * person's height above it. The SURFACE band's ceiling is half a kilometre up,
 * and from there every rock in the disc is 300 to 500 m away: the two finest
 * bands' 17,000 instances land at 0.17 to 1.04 px each and draw as a field of
 * lit points over ground the sun does not reach - the pepper-like noise the
 * band design exists to avoid, arriving from the other end. Below this a rock
 * is aliasing rather than a mark on the ground, so the band is not drawn at
 * all. The band's members are 2 px and over where it switches, which is what
 * keeps the switch from reading as a pop.
 */
export const ROCK_MIN_PX = 2;

/** x, z, radius, baseY, standing height, meshR (the geometry's own bound). */
export const OBSTACLE_STRIDE = 6;
/** Below this a rock is something you drive over, not something you climb. */
const OBSTACLE_MIN_D = 0.35;

/**
 * Diameter at which a rock stops being ground and starts being in the way.
 *
 * Off the field's OWN draw rather than chosen: at Jezero a 9,340-instance fill
 * is 47% wider than the obstacle set's 0.35 m, 29% wider than 0.60 m and 5.5%
 * wider than this, and a rock this wide stands about 0.24 m proud of its base
 * (`d * squash * (0.5 - burial)`), which is the rover's own wheel RADIUS. So
 * this is where a rock changes from a bump a wheel rolls over into a step it
 * cannot climb, and it is the same number for the walkaround: you do not walk
 * your viewpoint through something knee-high.
 */
export const BLOCK_MIN_D = 1.0;

/**
 * Side of the blocker lookup's cell, in metres.
 *
 * Wider than the widest rock the law draws (2.5 m at Jezero, 15 m at the
 * clamp) so a rock spans at most two cells on an axis, and wide enough that the
 * whole near disc is a few hundred cells rather than tens of thousands.
 */
const BLOCK_CELL_M = 16;

/** Where a query left the point, in plot-local metres. */
export interface RockPush {
  x: number;
  z: number;
  /** Metres the deepest overlap needed. 0 when nothing was in the way. */
  depth: number;
}

export interface RockField {
  group: THREE.Group;
  /** See OBSTACLE_STRIDE. Rebuilt with the scatter. */
  obstacles: Float32Array;
  /** Instances currently drawn, across all bands. */
  count: number;
  /** Smallest rock currently drawn anywhere. */
  minDiameterM: number;
  /**
   * Regenerate whichever bands the camera has outrun. Returns true if any did.
   *
   * A rebuild runs the staged fill's own resumable slice, one a frame, so a
   * walked threshold does not land a whole band on one frame.
   */
  update(centre: { x: number; z: number }): boolean;
  /**
   * The first fill, one band to a frame rather than all four in one.
   *
   * Four unbuilt bands is 25,000 instances and read 99.8 ms on the frame the
   * plot mesh released its hold - the whole of what a PLOT to SURFACE handoff
   * still cost once the texture uploads were banded. The obstacle set is
   * gathered across the same frames and published whole at the end, so a rover
   * sees the same set a single-frame fill would have left.
   */
  fillStaged(
    centre: { x: number; z: number },
    nextFrame: () => Promise<void>,
    alive: () => boolean,
  ): Promise<void>;
  /** Cast onto the cascades at the SURFACE band only - src/atlas.ts flips this
   *  at the regime edge. Rocks never receive: only the terrain does. */
  setCastShadow(on: boolean): void;
  /**
   * Draw no rock that covers less than `ROCK_MIN_PX`.
   *
   * `minDiamPerMetre` is the diameter one of those pixels covers per metre of
   * range, and `nearestM` is the range to the closest ground there is - the eye's
   * own height over it. Every instance is held to the first in the vertex
   * shader; a band whose LARGEST member is under it even at the nearest range
   * cannot draw anything at all, so it is dropped whole and costs no vertex.
   * Zero draws every rock, which is what a caller with no pane to measure
   * against gets.
   */
  setPixelFloor(minDiamPerMetre: number, nearestM: number): void;
  /**
   * Slide a plot-local point out of every boulder its footprint overlaps.
   *
   * `y` is in DRAWN metres above the plot's own zero, the frame
   * `DtmPlot.meshHeightAt` answers in, and a rock only counts while the point
   * is under its top: step over a rock and it stops being in the way, which is
   * what keeps a hill of them from being a wall.
   *
   * Every overlap is summed into one correction rather than the deepest being
   * resolved and the set re-tested. Two rocks in contact would otherwise hand
   * the point back and forth between them on alternate frames, which is the
   * jitter a push-out is usually blamed for.
   *
   * Answers whether `out` differs from the point it was given.
   */
  pushOut(x: number, z: number, y: number, bodyR: number, out: RockPush): boolean;
  /** Blockers currently indexed: rocks at or over `BLOCK_MIN_D`. */
  readonly blockerCount: number;
  /**
   * One blocker as [x, z, radius, baseY, height, meshR], or null.
   *
   * `radius` is the analytic bound `pushOut` collides against; `meshR` is the
   * same rock's actual drawn geometry measured independently of it, so a
   * check can tell a stored radius that undershoots the mesh from one that
   * does not.
   */
  blocker(i: number): [number, number, number, number, number, number] | null;
  /**
   * Instances the staged mount fill's last slice placed, and the largest any
   * one of its slices placed since the counters were zeroed. Dojo readings,
   * not player state; a walked threshold's own refill never writes these -
   * see `fillSliceRefillLast`/`Max` below.
   */
  readonly fillSliceLast: number;
  readonly fillSliceMax: number;
  /** `FILL_SLICE_INSTANCES` itself, so a check reads the budget rather than
   *  carrying its own copy of the number. */
  readonly fillSliceBudget: number;
  /** Band-threshold rebuilds started - what says a walk crossed one. */
  readonly fillSliceRefills: number;
  /** Instances those rebuilds have placed. */
  readonly fillSliceRefillInstances: number;
  /**
   * A walked threshold's own per-frame worst/last placed total - kept apart
   * from `fillSliceLast`/`Max` above so a reading of one path can never pick
   * up the other's instances, whichever ran more recently.
   */
  readonly fillSliceRefillLast: number;
  readonly fillSliceRefillMax: number;
  /**
   * Which band a walked crossing most recently started a rebuild for, or -1
   * before any. Three of the four bands never move on a single crossing, so
   * a reading over `count`/`obstacles`' four-band total dilutes a real
   * per-band collapse into their flat counts - the pre-fix bug's own ~95-98%
   * drop in the crossed band read as a ~35% dip in the aggregate.
   */
  readonly fillSliceRefillBand: number;
  /** That band's own drawn/obstacle counts read at the crossing, before any
   *  slice of the new fill touched it - the "before" a per-band check compares. */
  readonly fillSliceRefillBandBaseDrawn: number;
  readonly fillSliceRefillBandBaseObstacles: number;
  /** That band's own live drawn/obstacle counts, polled mid-refill. */
  readonly fillSliceRefillBandDrawn: number;
  readonly fillSliceRefillBandObstacles: number;
  /** Zero the walked-refill counters above, so a walk is read on its own. */
  resetFillSliceStats(): void;
  dispose(): void;
}

/**
 * Create a plot's boulder field.
 *
 * Placement is biased by the terrain the Sampler already measures: rocks
 * concentrate where slopes shed them and thin out on the smooth pans, which is
 * what makes the scatter agree with the surface it sits on rather than being
 * sprinkled over it.
 */
export function createRockField(
  field: PlotField,
  stats: RockStats,
  /** The focused body's own fit. Null means the registry declares none for it,
   *  and no scatter is drawn rather than one on another body's coefficients. */
  law: RockLaw | null,
  uniforms: Record<string, THREE.IUniform>,
  seed: number,
  tuning: RockTuning = ROCK_DEFAULTS,
  /**
   * Where the surface mesh DRAWS the ground, plot-local xz to exaggerated
   * metres - `DtmPlot.meshHeightAt`. Absent, the height field's own level 0,
   * which is a surface the triangles never reach: box-filtering four taps a
   * lattice step apart used to stand in for the mesh here and lifted the p50
   * rock 0.75 m off it, proportionally to the slope under it.
   */
  meshHeightAt: ((x: number, z: number) => number) | null = null,
): RockField | null {
  const k = stats.k_used ?? stats.k;
  if (!(k > 0) || stats.k_source === 'none' || law === null) return null;
  // Narrowed once: the band closures below outlive the guard's flow analysis.
  const fit: RockLaw = law;

  // The site's lithologies. Which stone is here is art direction informed by
  // published geology, kept deliberately separate from the measured abundance —
  // see rockSpecies.ts.
  const palette = paletteFor(field.meta.slug).filter((e) => SPECIES[e.species]);
  const species = palette.map((e) => SPECIES[e.species]!);
  const wTotal = palette.reduce((a, e) => a + e.weight, 0) || 1;
  const weights = palette.map((e) => e.weight / wTotal);

  // Geometry per (species, band): the shape depends on the lithology and the
  // tessellation on the size, so both axes matter.
  const geoms: THREE.BufferGeometry[][] = species.map((sp, si) =>
    BANDS.map((b, bi) => rockGeometry(
      tuning.burial,
      detailForSize(shapeDiameter(b.hi, b, tuning)),
      17 + bi * 7 + si * 101,
      sp,
    )));
  // The geometry's own worst-case horizontal reach at unit scale, measured off
  // its actual vertices rather than assumed from `elongate`, one per
  // (species, band), cached once so `meshR` below is a lookup, not a scan.
  const meshR: number[][] = geoms.map((row) => row.map((g) => {
    const p = g.getAttribute('position') as THREE.BufferAttribute;
    let r = 0;
    for (let i = 0; i < p.count; i++) r = Math.max(r, Math.hypot(p.getX(i), p.getZ(i)));
    return r;
  }));
  /** Metres of diameter one pixel covers per metre of range; 0 draws every rock. */
  const minDiamPerM: THREE.IUniform = { value: 0 };
  /** `--boulders --inject flat-hue`: the tone off the ground, withdrawn. */
  const groundTone: THREE.IUniform = { value: rockDevOff('rocktone') ? 0 : 1 };
  /** `--boulders --inject unlit-rocks`: no sun direction on a rock at all. */
  const litDirect: THREE.IUniform = { value: rockDevOff('rocklit') ? 0 : 1 };
  /** `--boulders --inject no-bounce`: the shadow side back on sky fill alone. */
  const bounceOn: THREE.IUniform = { value: rockDevOff('rockbounce') ? 0 : 1 };
  /** `--boulders --inject no-rock-collision`: the boulders porous again. */
  const blockOff = rockDevOff('rockblock');
  /**
   * `--boulders --inject late-obstacles`: the set published once at the end.
   *
   * The pre-fix shape, where a staged fill cut short by the mount handing over
   * left the walkaround with no obstacles at all.
   */
  const publishEverySlice = !rockDevOff('rockpublish');
  /**
   * `--boulders --inject rock-batch-whole`: the per-frame instance budget
   * withdrawn, so a staged fill's first slice places a whole band at once.
   */
  const sliceInstances = rockDevOff('rockslice') ? Infinity : FILL_SLICE_INSTANCES;
  /** `--ship --inject rock-refill-whole`: a walked threshold's band filled whole again. */
  const refillBudgeted = !rockDevOff('rockrefill');
  /** `fillSliceLast`/`fillSliceMax` below: the reading `--inject rock-batch-whole` has to move.
   *  Written only by `fillStaged` - `update`'s own refill has its own pair below it never touches. */
  let fillSliceLast = 0;
  let fillSliceMax = 0;
  /** Band-threshold rebuilds started, and the instances they have placed. */
  let refillCount = 0;
  let refillInstances = 0;
  /** `update`'s own walked-refill worst/last PER-FRAME total, apart from `fillSliceLast`/`Max`. */
  let refillSliceLast = 0;
  let refillSliceMax = 0;
  /** The band a walked crossing last started a rebuild for, and its own
   *  counts read right then - see `fillSliceRefillBand` above. */
  let refillBandIndex = -1;
  let refillBandBaseDrawn = 0;
  let refillBandBaseObstacles = 0;
  function resetFillCounters(): void {
    fillSliceLast = 0;
    fillSliceMax = 0;
  }
  function resetRefillCounters(): void {
    refillCount = 0;
    refillInstances = 0;
    refillSliceLast = 0;
    refillSliceMax = 0;
    refillBandIndex = -1;
    refillBandBaseDrawn = 0;
    refillBandBaseObstacles = 0;
  }
  const mats = species.map((sp) =>
    rockMaterial(uniforms, sp, minDiamPerM, groundTone, litDirect, bounceOn));
  const group = new THREE.Group();
  const places = field.placements;
  const minY = field.meta.elevation.min_m;

  // The regional bed: one dip azimuth and one gentle dip angle for the whole
  // plot, so every bedded plate on it agrees. Derived from the plot seed, so
  // the bed is as stable as everything else here.
  const bedRng = rng((seed ^ 0x9e3779b9) >>> 0);
  const dipAz = bedRng() * Math.PI * 2;
  const dipRad = (4 + bedRng() * 9) * Math.PI / 180;
  const bedNormal = new THREE.Vector3(
    Math.sin(dipRad) * Math.cos(dipAz),
    Math.cos(dipRad),
    Math.sin(dipRad) * Math.sin(dipAz),
  ).normalize();

  const share = tuning.budget / 25000;
  const bands: Band[] = BANDS.map((b, bi) => {
    const budget = Math.max(1, Math.round(b.budget * share));
    const density = bandDensity(b, k, fit, tuning);
    // Shrink the radius rather than overrun the budget, so rocky ground draws a
    // smaller circle at the CORRECT density instead of a large one at the wrong
    // density.
    const rMax = Math.sqrt(budget / Math.max(density * Math.PI, 1e-9));
    const radius = Math.min(b.reach, rMax);
    const n = Math.min(budget, Math.ceil(density * Math.PI * radius * radius) + 8);
    const meshes = species.map((_sp, si) => {
      // Each species gets its share of the band plus headroom, because the
      // draw is random and a run of one lithology must not overflow its mesh.
      const cap = Math.max(1, Math.ceil(n * weights[si]! * 1.45) + 8);
      const m = new THREE.InstancedMesh(geoms[si]![bi]!, mats[si]!, cap);
      m.count = 0;
      m.castShadow = m.receiveShadow = false;
      group.add(m);
      return m;
    });
    let acc = 0;
    const cum = weights.map((w) => (acc += w));
    return { meshes, cum, radius, cx: NaN, cz: NaN, built: false };
  });

  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const spin = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const nrm = new THREE.Vector3();

  /**
   * A band's fill in flight, so the first one can be cut across frames.
   *
   * `limit` instances to a slice; the cell list, the per-species counts and
   * the running total carry over so a resumed slice places exactly the rocks
   * the single-frame fill would have placed in the same order. `mid` is a
   * slice paused INSIDE a cell rather than between two of them - the cell's
   * own RNG stream and the candidate index it had reached, so a dense cell
   * spanning several slices resumes where it left off rather than restarting.
   */
  interface BandResume {
    cells: Array<{ x: number; z: number; d2: number }> | null;
    placedPer: number[];
    cursor: number;
    placed: number;
    limit: number;
    done: boolean;
    mid: { r: () => number; n: number; i: number } | null;
    /**
     * Set only for a refill of an ALREADY-BUILT band: the new fill
     * accumulates here instead of the live mesh and obstacle segment, so the
     * band being replaced stays fully drawn and collidable until the last
     * slice commits it in one swap. Absent for priming and the mount's own
     * staged fill, which have no prior content to protect.
     */
    stage?: BandStage;
  }

  /** A band refill's own working set while it stages, sized like the live mesh
   *  and obstacle segment it will replace on the swap. */
  interface BandStage {
    mats: Float32Array[];
    obstacles: Float32Array;
    obsN: number;
  }

  /**
   * One band's rebuild in flight, with the centre its cell list was cut around.
   *
   * A later crossing replaces the request outright, so the newer one wins and
   * no slice of the abandoned resume is ever run again - its stage, if any,
   * is simply dropped, leaving the currently drawn band untouched.
   */
  interface PendingRefill { resume: BandResume; cx: number; cz: number; }
  const refills: Array<PendingRefill | null> = BANDS.map(() => null);

  function freshResume(limit: number, stage?: BandStage): BandResume {
    return { cells: null, placedPer: [], cursor: 0, placed: 0, limit, done: false, mid: null, stage };
  }

  function fillBand(bi: number, cx: number, cz: number, resume?: BandResume): number {
    const b = BANDS[bi]!;
    const band = bands[bi]!;
    const density = bandDensity(b, k, fit, tuning);
    const perCell = density * CELL_M * CELL_M;
    const radius = band.radius;
    const caps = band.meshes.map((m) => m.instanceMatrix.count);
    const placedPer = resume && resume.placedPer.length > 0
      ? resume.placedPer : band.meshes.map(() => 0);
    if (resume) resume.placedPer = placedPer;
    // Staging a refill of a band already drawn: writes below land in `stage`
    // instead of the live mesh and obstacle segment, which stay exactly as
    // they were - the caller only commits `stage` once this fill is `done`.
    const stage = resume?.stage;
    // This band's own obstacle segment, replaced wholesale by its own refill
    // (or `stage`'s own segment, when this is one). Cursor 0 alone is not
    // "the first call": a cell dense enough to outlast one slice's instance
    // budget pauses mid-cell and resumes at the SAME cursor, and a reset
    // there would drop what that cell already placed.
    if (!resume || (resume.cursor === 0 && !resume.mid)) {
      if (stage) stage.obsN = 0; else obsN[bi] = 0;
    }
    const cap = caps.reduce((a, c) => a + c, 0);
    // Band 0 is pebbles — thousands of them, none worth a collision test.
    const collect = bi > 0;
    const exag = Math.max(1e-6, uniforms['uExag']!.value as number);

    const c0x = Math.floor(cx / CELL_M);
    const c0z = Math.floor(cz / CELL_M);
    const span = Math.ceil(radius / CELL_M) + 1;
    const r2 = (radius + CELL_M) * (radius + CELL_M);
    const rad2 = radius * radius;
    let placed = resume ? resume.placed : 0;

    // NEAREST CELL FIRST.
    //
    // This loop used to run in raster order, and the budget is not generous
    // enough for that to be a detail: the near band's nine thousand instances
    // were consumed by the first seven of sixteen cells, which in raster order
    // are the FAR CORNER of the disc. Every pebble landed in one clump forty
    // metres off in -x/-z and the ground under the camera got nothing — a
    // scatter that was working perfectly, somewhere else.
    //
    // Sorted by distance, running out of budget costs the farthest rocks, which
    // is what running out of budget is supposed to cost.
    let cells = resume?.cells ?? null;
    if (cells === null) {
      cells = [];
      for (let ccz = c0z - span; ccz <= c0z + span; ccz++) {
        for (let ccx = c0x - span; ccx <= c0x + span; ccx++) {
          const mx = (ccx + 0.5) * CELL_M - cx;
          const mz = (ccz + 0.5) * CELL_M - cz;
          const d2 = mx * mx + mz * mz;
          if (d2 <= r2) cells.push({ x: ccx, z: ccz, d2 });
        }
      }
      cells.sort((a, b2) => a.d2 - b2.d2);
      if (resume) resume.cells = cells;
    }

    const stop = cells.length;
    let ci = resume ? resume.cursor : 0;
    // Instances placed by THIS call, not the whole staged fill: what the
    // per-slice budget below is checked against.
    let sliceCount = 0;
    let pausedForBudget = false;
    outer:
    {
      for (; ci < stop; ci++) {
        const cell = cells[ci]!;
        const ccx = cell.x;
        const ccz = cell.z;
        let r: () => number;
        let n: number;
        let iStart: number;
        if (resume && resume.mid) {
          ({ r, n, i: iStart } = resume.mid);
          resume.mid = null;
        } else {
          r = rng(cellSeed(ccx, ccz, bi, seed));
          // Fractional counts must not be truncated, or a density below one
          // rock per cell rounds to an empty world.
          n = Math.floor(perCell) + (r() < perCell % 1 ? 1 : 0);
          iStart = 0;
        }

        for (let i = iStart; i < n; i++) {
          if (placed >= cap) break outer;
          if (resume && sliceCount >= resume.limit) {
            // Pause INSIDE this cell: cursor stays put and `mid` carries the
            // live RNG closure plus where it had reached, so the next call
            // resumes this exact candidate rather than re-rolling the cell.
            resume.mid = { r, n, i };
            pausedForBudget = true;
            break outer;
          }

          const x = (ccx + r()) * CELL_M;
          const z = (ccz + r()) * CELL_M;

          // Inside the DISC, not merely inside a cell that overlaps it. A cell
          // straddling the edge would otherwise emit its whole quota, which put
          // the potential count at two and a half times the budget and made the
          // radius that was solved for the budget meaningless.
          const ox = x - cx;
          const oz = z - cz;
          if (ox * ox + oz * oz > rad2) continue;

          let inside = false;
          for (const place of places) {
            if (field.insideHex(x - place.x_m, z - place.z_m)) { inside = true; break; }
          }
          if (!inside) continue;

          // slopeAt is in DEGREES. Broken, shedding ground carries more rock
          // than a pan, so bias toward it — bounded, because this modulates a
          // measured abundance rather than replacing it with a slope map.
          if (r() > Math.min(1, 0.35 + field.slopeAt(x, z) / Math.max(1, tuning.slopeDeg))) continue;

          // The instance matrix carries the UNEXAGGERATED height above the
          // plot minimum, because the rock's own vertex shader applies uExag
          // to it; meshHeightAt answers in drawn metres, and heightAt is
          // elevation above the datum.
          const y = meshHeightAt
            ? meshHeightAt(x, z) / exag
            : field.heightAt(x, z) - minY;
          if (!Number.isFinite(y)) continue;

          // Uniform in area within the band, then mapped through the law so the
          // distribution inside the band is right, not merely bounded by it.
          const u = r();
          const q = qOfK(k, fit);
          let d = -Math.log(
            Math.exp(-q * b.lo) * (1 - u) + Math.exp(-q * b.hi) * u,
          ) / q;
          d = shapeDiameter(d, b, tuning);

          pos.set(x, y, z);
          // Which lithology this rock is, from the cell's own stream so the
          // species is as stable as the position. Chosen BEFORE it is turned,
          // because bedrock and float rock are oriented by different rules.
          const u2 = r();
          let si = band.cum.length - 1;
          for (let c = 0; c < band.cum.length; c++) {
            if (u2 <= band.cum[c]!) { si = c; break; }
          }
          if (placedPer[si]! >= caps[si]!) continue;

          if (species[si]!.bedded) {
            // An exposed bed is not rubble. Every plate in it was laid down in
            // the same water and shares one dip, so scattering them with random
            // rotations reads instantly as debris — which is exactly what the
            // Kimberley strata are not. They take the REGIONAL bedding normal
            // with only a few degrees of jitter, and spin freely about it,
            // since rotating within the bedding plane is still lying in the bed.
            nrm.copy(bedNormal);
            nrm.x += (r() - 0.5) * 0.10;
            nrm.z += (r() - 0.5) * 0.10;
            nrm.normalize();
          } else {
            field.normalAt(x, z, nrm);
            r(); r();   // keep the stream aligned across both branches
          }
          quat.setFromUnitVectors(up, nrm);
          spin.setFromAxisAngle(nrm, r() * Math.PI * 2);
          quat.premultiply(spin);
          // Bedded rock is a slab: it keeps its own squash and does not get the
          // extra vertical jitter a tumbled block gets.
          const vy = species[si]!.bedded ? 0.92 : 0.55 + r() * 0.4;
          scl.set(d, d * vy, d);
          m.compose(pos, quat, scl);
          if (stage) m.toArray(stage.mats[si]!, placedPer[si]! * 16);
          else band.meshes[si]!.setMatrixAt(placedPer[si]!, m);
          placedPer[si]!++;
          placed++;
          sliceCount++;

          if (collect && d >= OBSTACLE_MIN_D) {
            const n = stage ? stage.obsN : obsN[bi]!;
            if (n < obsCap[bi]!) {
              const buf = stage ? stage.obstacles : obstacleBuf;
              const o = (stage ? 0 : obsBase[bi]!) + n;
              const ob = o * OBSTACLE_STRIDE;
              buf[ob] = x;
              buf[ob + 1] = z;
              // The long semi-axis, not the short: elongate stretches the drawn
              // footprint along a randomly yawed axis, and a circle any smaller
              // lets a mover clip into the mesh on that heading.
              buf[ob + 2] = d * 0.5 * species[si]!.elongate;
              buf[ob + 3] = y;
              // Standing height above its base. The geometry is a unit sphere
              // of radius 0.5 shifted down by the burial, so its top sits at
              // (0.5 - burial) in local units before the instance scale.
              buf[ob + 4] = scl.y * (0.5 - 0.18);
              // The rock's true horizontal reach at this instance's own scale,
              // read off the drawn geometry rather than assumed from
              // `elongate`, what a check tests the radius above against.
              buf[ob + 5] = meshR[si]![bi]! * d;
              if (stage) stage.obsN++; else obsN[bi] = n + 1;
            }
          }
        }
      }
    }

    if (resume) {
      resume.cursor = ci;
      resume.placed = placed;
      resume.done = !pausedForBudget && (ci >= cells.length || placed >= cap);
    }

    if (stage) {
      // Nothing live moves until the whole band's new fill is in hand: the
      // band being replaced keeps drawing and colliding at its old count
      // right up to this frame, then swaps whole rather than growing in.
      if (!resume!.done) return sliceCount;
      for (let si = 0; si < band.meshes.length; si++) {
        band.meshes[si]!.instanceMatrix.array.set(stage.mats[si]!);
        band.meshes[si]!.count = placedPer[si]!;
        band.meshes[si]!.instanceMatrix.needsUpdate = true;
        band.meshes[si]!.computeBoundingSphere();
      }
      obstacleBuf.set(
        stage.obstacles.subarray(0, stage.obsN * OBSTACLE_STRIDE),
        obsBase[bi]! * OBSTACLE_STRIDE,
      );
      obsN[bi] = stage.obsN;
      band.cx = cx;
      band.cz = cz;
      band.built = true;
      return sliceCount;
    }

    for (let si = 0; si < band.meshes.length; si++) {
      band.meshes[si]!.count = placedPer[si]!;
      band.meshes[si]!.instanceMatrix.needsUpdate = true;
    }
    // Cull against where the instances actually ARE. Left unset, three falls
    // back to the geometry's own bounds — a half-metre sphere at the plot
    // origin — and the whole band pops out of existence whenever the centre of
    // the plot leaves the frustum, which is most of the time once you are close
    // enough to see a rock.
    for (const m2 of band.meshes) m2.computeBoundingSphere();
    if (resume && !resume.done) return sliceCount;
    band.cx = cx;
    band.cz = cz;
    band.built = true;
    return sliceCount;
  }

  // the obstacle set
  //
  // Gathered into one segment PER BAND rather than into one running list.
  //
  // A rebuild refills only the bands the camera outran, and the list used to be
  // reset on the first of them: one near-band rebuild and the only rocks
  // anything could hit were that band's 0.25 to 0.6 m cobbles, with every
  // boulder in the field gone from the set. Segments mean a band's refill
  // replaces its own rocks and leaves the other bands' where they were.
  const obsCap = BANDS.map((b, i) =>
    (i === 0 ? 0 : Math.max(1, Math.round(b.budget * share)) + 8));
  const obsBase: number[] = [];
  {
    let at = 0;
    for (const c of obsCap) { obsBase.push(at); at += c; }
  }
  const obstacleBuf = new Float32Array(
    obsBase.reduce((a, b, i) => Math.max(a, b + obsCap[i]!), 0) * OBSTACLE_STRIDE);
  const obsN = BANDS.map(() => 0);
  /** The segments packed end to end, which is what `obstacles` hands out. */
  let packed = new Float32Array(0);
  let packedN = 0;

  /** A fresh staging area for band `bi`'s refill, sized like the live mesh and
   *  obstacle segment it will replace. */
  function makeBandStage(bi: number): BandStage {
    return {
      mats: bands[bi]!.meshes.map((mesh) => new Float32Array(mesh.instanceMatrix.array.length)),
      obstacles: new Float32Array(obsCap[bi]! * OBSTACLE_STRIDE),
      obsN: 0,
    };
  }

  function publishObstacles(): Float32Array {
    let n = 0;
    for (let i = 0; i < BANDS.length; i++) n += obsN[i]!;
    const out = new Float32Array(n * OBSTACLE_STRIDE);
    let w = 0;
    for (let i = 0; i < BANDS.length; i++) {
      const from = obsBase[i]! * OBSTACLE_STRIDE;
      const len = obsN[i]! * OBSTACLE_STRIDE;
      out.set(obstacleBuf.subarray(from, from + len), w);
      w += len;
    }
    packed = out;
    packedN = n;
    buildBlockers();
    return out;
  }

  // the blocker lookup
  //
  // A uniform grid over the rocks at or over BLOCK_MIN_D, rebuilt with the
  // scatter and never per frame. The whole point is that a query touches the
  // handful of cells its own footprint covers rather than the four thousand
  // rocks the obstacle set holds: the rover already scans all of them for six
  // wheels every frame, and the camera would have made that a seventh.
  let blockIdx = new Int32Array(0);
  let blockN = 0;
  let cellStart = new Int32Array(0);
  let cellItems = new Int32Array(0);
  let cellCursor = new Int32Array(0);
  let gridX0 = 0;
  let gridZ0 = 0;
  let gridNX = 0;
  let gridNZ = 0;

  function buildBlockers(): void {
    const o = packed;
    const minR = BLOCK_MIN_D * 0.5;
    let n = 0;
    for (let i = 0; i < packedN; i++) if (o[i * OBSTACLE_STRIDE + 2]! >= minR) n++;
    if (blockIdx.length < n) blockIdx = new Int32Array(Math.max(n, 256));
    blockN = 0;
    let x0 = Infinity;
    let z0 = Infinity;
    let x1 = -Infinity;
    let z1 = -Infinity;
    for (let i = 0; i < packedN; i++) {
      const b = i * OBSTACLE_STRIDE;
      if (o[b + 2]! < minR) continue;
      blockIdx[blockN++] = b;
      if (o[b]! < x0) x0 = o[b]!;
      if (o[b]! > x1) x1 = o[b]!;
      if (o[b + 1]! < z0) z0 = o[b + 1]!;
      if (o[b + 1]! > z1) z1 = o[b + 1]!;
    }
    if (blockN === 0) { gridNX = gridNZ = 0; return; }
    gridX0 = x0;
    gridZ0 = z0;
    gridNX = Math.max(1, Math.ceil((x1 - x0) / BLOCK_CELL_M) + 1);
    gridNZ = Math.max(1, Math.ceil((z1 - z0) / BLOCK_CELL_M) + 1);
    const cells = gridNX * gridNZ;
    if (cellStart.length < cells + 1) cellStart = new Int32Array(cells + 1);
    else cellStart.fill(0, 0, cells + 1);
    if (cellItems.length < blockN) cellItems = new Int32Array(Math.max(blockN, 256));
    if (cellCursor.length < cells) cellCursor = new Int32Array(cells);
    else cellCursor.fill(0, 0, cells);
    // Counting sort: count per cell, prefix-sum into starts, then place.
    for (let i = 0; i < blockN; i++) {
      const b = blockIdx[i]!;
      const c = cellOf(o[b]!, o[b + 1]!);
      cellStart[c + 1] = cellStart[c + 1]! + 1;
    }
    for (let c = 0; c < cells; c++) cellStart[c + 1] = cellStart[c + 1]! + cellStart[c]!;
    for (let i = 0; i < blockN; i++) {
      const b = blockIdx[i]!;
      const c = cellOf(o[b]!, o[b + 1]!);
      cellItems[cellStart[c]! + cellCursor[c]!] = b;
      cellCursor[c] = cellCursor[c]! + 1;
    }
  }

  function cellOf(x: number, z: number): number {
    const cx = Math.min(gridNX - 1, Math.max(0, Math.floor((x - gridX0) / BLOCK_CELL_M)));
    const cz = Math.min(gridNZ - 1, Math.max(0, Math.floor((z - gridZ0) / BLOCK_CELL_M)));
    return cz * gridNX + cx;
  }

  /** A hair outside the rock, so a point resting on one is not re-solved every frame. */
  const BLOCK_SKIN_M = 0.02;

  /** One band's own drawn instance count, live - what a per-band partial-swap
   *  reading compares against `refillBandBaseDrawn`. */
  function bandDrawnCount(bi: number): number {
    return bands[bi]!.meshes.reduce((x, m2) => x + (m2.visible ? m2.count : 0), 0);
  }

  const api: RockField = {
    group,
    obstacles: new Float32Array(0),
    count: 0,
    minDiameterM: BANDS[0]!.lo,
    update(centre): boolean {
      // The first fill is all four bands, and it lands on the frame the plot
      // mesh is first drawn on - 40 ms of scatter on the one frame of the
      // descent that also links the species' programs. One band a frame while
      // the field comes up, so neither cost meets the other.
      const priming = bands.some((b) => !b.built);
      let changed = false;
      // Summed across whatever this ONE call actually runs, so the reading is
      // this real frame's total cost rather than the largest single fillBand
      // call in it - the two only coincide while the loop below never runs
      // more than one refill per frame, which is a property of the loop, not
      // of the counter.
      let frameTotal = 0;
      let framePlaced = false;
      for (let i = 0; i < bands.length; i++) {
        const band = bands[i]!;
        if (priming) {
          if (band.built) continue;
          // No reset here: each band appends its own obstacles once, so the
          // set after the last band is the one the all-at-once fill left.
          changed = true;
          fillBand(i, centre.x, centre.z);
          break;
        }
        // Each band chases the camera on its OWN threshold. The near band is
        // small and cheap and may rebuild often; the far band holds thousands
        // of metres of boulders and almost never needs to. Rebuilding all of
        // them together would put the far band's cost on the near band's
        // schedule.
        //
        // Measured from the centre an in-flight rebuild was asked for, not from
        // `band.cx` (the last COMPLETED fill), which would re-request every frame.
        const at = refills[i];
        const moved = Math.hypot(centre.x - (at ? at.cx : band.cx),
                                 centre.z - (at ? at.cz : band.cz)) > band.radius * 0.3;
        if (!moved) continue;
        // The band this crossing touches, and its own count right now - before
        // this frame's fillBand call (below or staged) can move it.
        refillBandIndex = i;
        refillBandBaseDrawn = bandDrawnCount(i);
        refillBandBaseObstacles = obsN[i]!;
        if (!refillBudgeted) {
          // `--inject rock-refill-whole`: the whole band on this frame again.
          // No stage needed - a single synchronous call replaces the band
          // wholesale with nothing else able to read it mid-call.
          changed = true;
          const n = fillBand(i, centre.x, centre.z);
          frameTotal += n;
          framePlaced = true;
          refillCount++;
          refillInstances += n;
          continue;
        }
        // A fresh crossing always replaces any refill already in flight for
        // this band outright: the old stage, if any, is simply dropped, which
        // is what keeps the currently drawn band intact across a re-cross.
        refills[i] = { resume: freshResume(sliceInstances, makeBandStage(i)), cx: centre.x, cz: centre.z };
        refillCount++;
      }
      if (!priming) {
        // ONE slice a frame for the whole field: a slice cannot place more than
        // its own `limit`, so this is the per-frame budget a walk lands under.
        for (let i = 0; i < bands.length; i++) {
          const r = refills[i];
          if (!r) continue;
          const n = fillBand(i, r.cx, r.cz, r.resume);
          frameTotal += n;
          framePlaced = true;
          refillInstances += n;
          changed = true;
          if (r.resume.done) refills[i] = null;
          break;
        }
      }
      if (framePlaced) {
        refillSliceLast = frameTotal;
        if (frameTotal > refillSliceMax) refillSliceMax = frameTotal;
      }
      if (changed) {
        // Published every slice, as the mount's staged fill does, so a walker
        // collides with the rocks already placed rather than the ones before.
        if (publishEverySlice || refills.every((r) => r === null)) {
          this.obstacles = publishObstacles();
        }
        this.count = bands.reduce(
          (a, b) => a + b.meshes.reduce((x, m2) => x + (m2.visible ? m2.count : 0), 0), 0);
      }
      return changed;
    },
    async fillStaged(centre, nextFrame, alive): Promise<void> {
      // A fresh window: the max below has to read this fill's own worst
      // frame, not one a previous plot's fill left behind.
      resetFillCounters();
      for (let i = 0; i < bands.length; i++) {
        const resume: BandResume = freshResume(sliceInstances);
        do {
          fillSliceLast = fillBand(i, centre.x, centre.z, resume);
          if (fillSliceLast > fillSliceMax) fillSliceMax = fillSliceLast;
          this.count = bands.reduce(
            (a, b) => a + b.meshes.reduce((x, m2) => x + m2.count, 0), 0);
          // Published every slice, not once at the end. A staged fill that is
          // cut short - the mount hands over, `alive()` goes false - used to
          // leave the set at its empty initial value until some later band
          // rebuild refilled it, so nothing could hit a rock at all on the
          // walkaround the descent actually arrived in.
          if (publishEverySlice) this.obstacles = publishObstacles();
          await nextFrame();
          if (!alive()) return;
        } while (!resume.done);
      }
      this.obstacles = publishObstacles();
    },
    setCastShadow(on): void {
      for (const b of bands) for (const m2 of b.meshes) m2.castShadow = on;
    },
    setPixelFloor(minDiamPerMetre, nearestM): void {
      minDiamPerM.value = Math.max(0, minDiamPerMetre);
      const floorM = Math.max(0, minDiamPerMetre) * Math.max(0, nearestM);
      let smallest = Infinity;
      let drawn = 0;
      for (let i = 0; i < bands.length; i++) {
        // The band's own LARGEST rock against the floor at the nearest range:
        // anything finer inside a band that survives is culled per instance in
        // the vertex shader, at its own range.
        const on = !(floorM > 0) || BANDS[i]!.hi >= floorM;
        for (const m2 of bands[i]!.meshes) {
          m2.visible = on;
          if (on) drawn += m2.count;
        }
        if (on) smallest = Math.min(smallest, Math.max(BANDS[i]!.lo, floorM));
      }
      // An upper bound: what the drawn bands hold, before the shader's own
      // per-instance cull takes the ones under a pixel out of them.
      this.count = drawn;
      this.minDiameterM = Number.isFinite(smallest) ? smallest : 0;
    },
    get blockerCount(): number {
      return blockN;
    },
    get fillSliceLast(): number {
      return fillSliceLast;
    },
    get fillSliceMax(): number {
      return fillSliceMax;
    },
    get fillSliceBudget(): number {
      return FILL_SLICE_INSTANCES;
    },
    get fillSliceRefills(): number {
      return refillCount;
    },
    get fillSliceRefillInstances(): number {
      return refillInstances;
    },
    get fillSliceRefillLast(): number {
      return refillSliceLast;
    },
    get fillSliceRefillMax(): number {
      return refillSliceMax;
    },
    get fillSliceRefillBand(): number {
      return refillBandIndex;
    },
    get fillSliceRefillBandBaseDrawn(): number {
      return refillBandBaseDrawn;
    },
    get fillSliceRefillBandBaseObstacles(): number {
      return refillBandBaseObstacles;
    },
    get fillSliceRefillBandDrawn(): number {
      return refillBandIndex < 0 ? 0 : bandDrawnCount(refillBandIndex);
    },
    get fillSliceRefillBandObstacles(): number {
      return refillBandIndex < 0 ? 0 : obsN[refillBandIndex]!;
    },
    resetFillSliceStats(): void {
      resetRefillCounters();
    },
    blocker(i): [number, number, number, number, number, number] | null {
      if (i < 0 || i >= blockN) return null;
      const b = blockIdx[i]!;
      const o = packed;
      return [o[b]!, o[b + 1]!, o[b + 2]!, o[b + 3]!, o[b + 4]!, o[b + 5]!];
    },
    pushOut(x, z, y, bodyR, out): boolean {
      out.x = x;
      out.z = z;
      out.depth = 0;
      if (blockOff || blockN === 0 || gridNX === 0) return false;
      const o = packed;
      const exag = Math.max(1e-6, uniforms['uExag']!.value as number);
      const reach = bodyR + BLOCK_MIN_D;
      const cx0 = Math.min(gridNX - 1, Math.max(0, Math.floor((x - reach - gridX0) / BLOCK_CELL_M)));
      const cx1 = Math.min(gridNX - 1, Math.max(0, Math.floor((x + reach - gridX0) / BLOCK_CELL_M)));
      const cz0 = Math.min(gridNZ - 1, Math.max(0, Math.floor((z - reach - gridZ0) / BLOCK_CELL_M)));
      const cz1 = Math.min(gridNZ - 1, Math.max(0, Math.floor((z + reach - gridZ0) / BLOCK_CELL_M)));
      let sx = 0;
      let sz = 0;
      let deepest = 0;
      for (let cz = cz0; cz <= cz1; cz++) {
        const row = cz * gridNX;
        for (let cx = cx0; cx <= cx1; cx++) {
          const c = row + cx;
          const end = cellStart[c + 1]!;
          for (let k = cellStart[c]!; k < end; k++) {
            const b = cellItems[k]!;
            // Over its top is past it, not through it: a rock stops being in
            // the way the moment you are standing higher than it is.
            if (y >= o[b + 3]! * exag + o[b + 4]!) continue;
            const dx = x - o[b]!;
            const dz = z - o[b + 1]!;
            const r = o[b + 2]! + bodyR;
            const d2 = dx * dx + dz * dz;
            if (d2 >= r * r) continue;
            const d = Math.sqrt(d2);
            const need = r - d + BLOCK_SKIN_M;
            if (need > deepest) deepest = need;
            // Dead centre has no direction to leave by; the rock's own frame
            // gives one that is stable rather than a random draw.
            const ux = d > 1e-4 ? dx / d : 1;
            const uz = d > 1e-4 ? dz / d : 0;
            sx += ux * need;
            sz += uz * need;
          }
        }
      }
      if (deepest <= 0) return false;
      out.x = x + sx;
      out.z = z + sz;
      out.depth = deepest;
      return true;
    },
    dispose(): void {
      for (const row of geoms) for (const g of row) g.dispose();
      for (const m2 of mats) m2.dispose();
      for (const b of bands) for (const m2 of b.meshes) m2.dispose();
    },
  };
  return api;
}
